import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ConfigProvider, App as AntdApp } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import ukUA from 'antd/locale/uk_UA'
import enUS from 'antd/locale/en_US'
import roRO from 'antd/locale/ro_RO'
import 'dayjs/locale/uk'
import 'dayjs/locale/en'
import 'dayjs/locale/ro'
import 'dayjs/locale/ru'
import 'dayjs/locale/be'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration'
import i18n from './i18n'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { KeycloakProvider } from './contexts/KeycloakContext'

import App from './App.tsx'
import './index.css'
import './i18n'

// Підключаємо плагіни dayjs
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 хвилин
    },
  },
})

// Функція для отримання локалі Ant Design
const getAntLocale = (lng: string) => {
  switch (lng) {
    case 'uk':
      dayjs.locale('uk')
      return ukUA
    case 'en':
      dayjs.locale('en')
      return enUS
    case 'ro':
      dayjs.locale('ro')
      return roRO
    case 'be':
      dayjs.locale('be')
      return ukUA // Використовуємо українську як найближчу до білоруської
    default:
      dayjs.locale('uk')
      return ukUA
  }
}

// Компонент для локалізації та теми
const AppWithTheme = () => {
  const [locale, setLocale] = React.useState(getAntLocale(i18n.language || 'uk'))
  const { antdTheme } = useTheme()

  React.useEffect(() => {
    // Слухаємо зміни мови
    const handleLanguageChange = (lng: string) => {
      console.log('Language changed to:', lng)
      setLocale(getAntLocale(lng))
    }

    // Підписуємось на зміни мови
      i18n.on('languageChanged', handleLanguageChange)
    
      // Встановлюємо поточну мову
      handleLanguageChange(i18n.language)

    // Зберігаємо i18n в window для доступу в інших компонентах
    ;(window as any).i18n = i18n

    return () => {
        i18n.off('languageChanged', handleLanguageChange)
    }
  }, [])

  return (
    <ConfigProvider locale={locale} theme={antdTheme}>
      <AntdApp>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  )
}

// Компонент-обгортка для провайдерів
const AppWrapper = () => {
  return (
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <KeycloakProvider>
            <ThemeProvider>
              <AppWithTheme />
            </ThemeProvider>
          </KeycloakProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(<AppWrapper />) 